/**
 * Pawn movement rules (classic Quoridor):
 * 1. One orthogonal step to an adjacent cell (no wall between, not occupied).
 * 2. Face-to-face jump: if the adjacent cell holds the opponent pawn and the
 *    cell straight beyond it is on the board and not walled off, jump there.
 * 3. Diagonal sidestep: only when the straight jump is blocked (wall or board
 *    edge), move to the cells diagonally beside the opponent pawn, provided
 *    the step from the opponent's cell to that diagonal cell is not walled.
 *
 * Phase 2 layers (all data-driven; classic play with no gimmicks and no
 * swamp effect is byte-for-byte unchanged):
 * - Every candidate destination is passed through the ice-slide resolver
 *   with its final entry direction (gimmicks.js). The returned cells are
 *   FINAL resting cells, so `{type:'move', to}` always names where the pawn
 *   actually ends up (UI highlights match).
 * - A player whose effects.swamped flag is set has NO pawn moves this turn.
 * - The ninja's wall jump is NOT handled here: it is an explicit ability
 *   move (abilities.js), not an implicit relaxation of canStep.
 */

import { stepBlockedByWall } from './walls.js';
import { moveContextFor, pathContextFor } from './characters.js';
import { resolveSlide, cellKey } from './gimmicks.js';
import { bfsDistanceToRow } from './path.js';

const DIRS = [
  { dc: 0, dr: 1 },
  { dc: 0, dr: -1 },
  { dc: 1, dr: 0 },
  { dc: -1, dr: 0 },
];

function onBoard(boardSize, col, row) {
  return col >= 0 && row >= 0 && col < boardSize && row < boardSize;
}

/**
 * Can a pawn take a single step between two adjacent cells?
 * (Wall check only — occupancy is handled by the caller.)
 * This stays the classic rule: abilities that cross walls are modeled as
 * separate ability moves, never as a relaxation of this predicate.
 */
function canStep(wallSet, from, to) {
  return !stepBlockedByWall(wallSet, from, to);
}

/**
 * All destination cells the given player's pawn may move to.
 * Returns an array of plain { col, row } objects (final resting cells,
 * after any forced ice slide).
 */
export function pawnMovesFor(state, playerIndex) {
  const player = state.players[playerIndex];

  // Swamp effect: pawn movement is blocked for this player's next turn.
  // Hex curse: the whole turn is skipped, so likewise no pawn moves.
  if (player.effects && (player.effects.swamped || player.effects.hexed)) return [];

  const { boardSize, wallSet, occupied, gimmickMap } = moveContextFor(
    state,
    playerIndex
  );
  const me = player.pawn;

  // With forced ice slides the movement graph is DIRECTED, so unlike
  // classic play a plain pawn move could in principle leave the mover with
  // no route back to their goal (self-strand). Guard every destination with
  // the mover's own path context (same BFS the wall legality check uses).
  // gimmickMap === null (classic, or immune player) skips all of this.
  const pathCtx = gimmickMap ? pathContextFor(state, playerIndex) : null;

  const moves = [];
  const seen = new Set();
  // (dc, dr) is the direction of the FINAL micro-step into the cell —
  // that is the direction an ice tile keeps the pawn sliding in.
  const push = (col, row, dc, dr) => {
    let cell = { col, row };
    if (gimmickMap) {
      cell = resolveSlide(boardSize, wallSet, gimmickMap, occupied, cell, dc, dr);
      if (
        bfsDistanceToRow(boardSize, wallSet, cell, player.goalRow, pathCtx) < 0
      ) {
        return; // would strand the mover — illegal
      }
    }
    const key = cellKey(cell.col, cell.row);
    if (!seen.has(key)) {
      seen.add(key);
      moves.push(cell);
    }
  };

  for (const { dc, dr } of DIRS) {
    const adj = { col: me.col + dc, row: me.row + dr };
    if (!onBoard(boardSize, adj.col, adj.row)) continue;
    if (!canStep(wallSet, me, adj)) continue;

    if (!occupied.has(cellKey(adj.col, adj.row))) {
      push(adj.col, adj.row, dc, dr);
      continue;
    }

    // Opponent pawn on the adjacent cell: try the straight jump.
    const jump = { col: adj.col + dc, row: adj.row + dr };
    const jumpOnBoard = onBoard(boardSize, jump.col, jump.row);
    if (
      jumpOnBoard &&
      canStep(wallSet, adj, jump) &&
      !occupied.has(cellKey(jump.col, jump.row))
    ) {
      push(jump.col, jump.row, dc, dr);
      continue;
    }

    // Straight jump blocked (wall or board edge): diagonal sidesteps.
    // Perpendicular directions relative to (dc, dr):
    const perps =
      dc === 0
        ? [
            { dc: 1, dr: 0 },
            { dc: -1, dr: 0 },
          ]
        : [
            { dc: 0, dr: 1 },
            { dc: 0, dr: -1 },
          ];
    for (const p of perps) {
      const diag = { col: adj.col + p.dc, row: adj.row + p.dr };
      if (!onBoard(boardSize, diag.col, diag.row)) continue;
      if (!canStep(wallSet, adj, diag)) continue;
      if (occupied.has(cellKey(diag.col, diag.row))) continue;
      // Entry direction is the perpendicular step (opponent cell -> diag).
      push(diag.col, diag.row, p.dc, p.dr);
    }
  }

  return moves;
}
